import { Color3 } from "@babylonjs/core";

/** Golden hour (spec 2d §3). Azimuth is clockwise from north (east = 90°). */
export const SUN = { azimuthDeg: 245, elevationDeg: 18, color: "#ffc98a", intensity: 1.6 } as const;
export const HEMI = { sky: "#ffd9b0", ground: "#6b4a34", intensity: 0.5 } as const;
export const SKY_STOPS: readonly { elevationDeg: number; color: string }[] = [
  { elevationDeg: 0, color: "#f2c39b" },
  { elevationDeg: 15, color: "#f7dcae" },
  { elevationDeg: 90, color: "#86a9c8" },
];
export const FOG = { density: 0.006, color: "#f2c39b" } as const;
export const LEAF_COLORS = ["#7f9a45", "#94a24c"] as const;
export const AMBER_LEAF = "#c08a3e";
export const AMBER_SHARE = 0.15;
export const SHADOW_FRUSTUM = 80;

/** Sky colour looking `elevationDeg` above the horizon; below the horizon it is the horizon colour. */
export function skyColorAt(elevationDeg: number): [number, number, number] {
  const e = Math.min(90, Math.max(0, elevationDeg));
  for (let i = 1; i < SKY_STOPS.length; i++) {
    const a = SKY_STOPS[i - 1]!, b = SKY_STOPS[i]!;
    if (e <= b.elevationDeg) {
      const t = (e - a.elevationDeg) / (b.elevationDeg - a.elevationDeg);
      const ca = Color3.FromHexString(a.color), cb = Color3.FromHexString(b.color);
      return [ca.r + (cb.r - ca.r) * t, ca.g + (cb.g - ca.g) * t, ca.b + (cb.b - ca.b) * t];
    }
  }
  const last = Color3.FromHexString(SKY_STOPS[SKY_STOPS.length - 1]!.color);
  return [last.r, last.g, last.b];
}

/** Unit vector the sunlight travels along (from the sun toward the ground); x east, y up, z north. */
export function sunDirection(): [number, number, number] {
  const az = (SUN.azimuthDeg * Math.PI) / 180, el = (SUN.elevationDeg * Math.PI) / 180;
  return [-Math.sin(az) * Math.cos(el), -Math.sin(el), -Math.cos(az) * Math.cos(el)];
}

/** Shadows cost a depth pass; skip them on touch devices and small backbuffers (spec 2d §3). */
export function shouldShadow(coarsePointer: boolean, cssWidth: number, dpr: number): boolean {
  return !coarsePointer && cssWidth * dpr >= 1200;
}

/** The glow layer is a full-screen pass every frame; touch devices go without it (spec 2p §3.7). */
export function shouldGlow(coarsePointer: boolean): boolean {
  return !coarsePointer;
}
